import { useEffect } from 'react';
import type { ArticleV1 as ArticleV1Type } from 'types/capi/article';

import Breadcrumbs from '~/components/BreadCrumbs';
import Content, { Constrainable } from '~/components/Content';
import DownloadAppBlock from '~/components/DownloadAppBlock';
import Image from '~/components/Image';
import RecommendedArticles from '~/components/RecommendedArticles';
import ViewNewsArticleEvent from '~/events/ViewNewsArticleEvent';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';

import AuthorInfo from './AuthorInfo';
import Badge from './Badge';
import Disclaimer from './Disclaimer';
import ShareIcons from './ShareIcons';
import Tags from './Tags';
import Title from './Title';

export default function ArticleV1() {
  const article = usePageData<ArticleV1Type>();
  const { clientIp, isBot } = useRootData();
  const user = useUser();

  useEffect(() => {
    new ViewNewsArticleEvent({
      article,
      clientIp,
      isBot,
      user
    }).send();
  }, [article, user, clientIp, isBot]);

  useStructuredData(article._schemas);

  return (
    <>
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-6 py-6 pb-10 lg:pb-14">
        <Breadcrumbs breadcrumbs={article.breadcrumbs} />
        <div className="relative flex flex-col items-center lg:mb-4">
          <Image
            caption={article.featured_image.caption}
            src={article.featured_image.url}
            alt={article.featured_image.alt}
            loading="eager"
            responsiveOptions={{
              lg: { height: 672, width: 1160 },
              md: { height: 450, width: 1024 },
              sm: { height: 450, width: 800 },
              xs: { height: 300, width: 672 }
            }}
          />
          <Badge badge={article.badge} />
        </div>

        <div className="mx-auto flex w-full flex-col items-center gap-4">
          <Constrainable constrain>
            <Title article={article} />
            <div className="mb-2 flex flex-col gap-6">
              <div className="flex w-full flex-row justify-between gap-2">
                <Tags tags={article.tags} />
                <ShareIcons />
              </div>
              <AuthorInfo author={article.author} date={article.date} />
              <Disclaimer />
            </div>
          </Constrainable>

          <Content constrainTextWidth={true} content={article.content} />
        </div>
      </div>
      {article.recent_articles && (
        <div className="bg-concrete">
          <RecommendedArticles
            articles={article.recent_articles}
            heading="Recent articles"
          />
        </div>
      )}

      <DownloadAppBlock
        downloadLink="https://thesolesupplier.onelink.me/BPkz/m35evxoc"
        qrCode="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/download_app_pdp_qr_code.png"
        pagePosition="Body"
      />
    </>
  );
}
